import { useEffect, useState, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useToast } from './useToast';
import { FlattenedUser } from '../types/user';
import { useSelector } from 'react-redux';
import {
	UserType,
	useOrgOnboardingModal_GetOrgUserQuery,
	useOrgOnboardingModal_UpdateSelfMutation,
} from '#types';
import { OrgOnboardingModalProps } from '../blocks/OrgOnboardingModal';
import rpcShared from '@rockpapercoin/rpc-shared';
import { useBlockingRouter } from './useBlockingRouter';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const orgOnboardingModal_GetOrgUser = gql`
	query orgOnboardingModal_GetOrgUser($where: OrgUserWhereUniqueInput!) {
		getOrgUserWhere(where: $where) {
			id
			user {
				id
				seenOnboardingModalAt
			}
			organization {
				id
				users {
					id
					role
				}
			}
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const orgOnboardingModal_UpdateOrgUser = gql`
	mutation orgOnboardingModal_UpdateSelf($data: UserUpdateSelfInput!) {
		updateSelf(data: $data) {
			id
			seenOnboardingModalAt
		}
	}
`;

export const useOrgOnboardingModal = (): OrgOnboardingModalProps & {
	setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>;
	isOrgOwner: boolean;
} => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError } = useToast();
	const [ open, setOpen ] = useState<undefined | boolean>();
	const [ isOrgOwner, setIsOrgOwner ] = useState( false );
	const router = useBlockingRouter();

	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	const [ updateSelf ] = useOrgOnboardingModal_UpdateSelfMutation();

	const apiResponse = useOrgOnboardingModal_GetOrgUserQuery( {
		variables: { where: { id: orgUser?.id || '' } },
		// wait until we have made the organization so that we can know who the org owner is
		skip: !orgUser?.organization?.id,
	} );

	// on mount, if we've just onboarded and are on the dashboard, show the modal
	useEffect( () => {
		// if signed in, and we haven't already run this, so we don't immediately hide the modal after just showing it
		if ( orgUser && typeof open === 'undefined' ) {
			if ( apiResponse.error ) {
				showError( apiResponse.error );
			} else if (
				apiResponse.data?.getOrgUserWhere?.organization &&
				router.pathname === '/'
			) {
				/* open the modal if
				- the org user hasn't seen it yet
				- and they are the org "owner" */
				setOpen(
					!apiResponse.data.getOrgUserWhere.user?.seenOnboardingModalAt &&
						rpcShared.organizations.isOwner(
							apiResponse.data.getOrgUserWhere.organization,
							apiResponse.data.getOrgUserWhere
						)
				);
			}
		}
	}, [
		router.pathname,
		apiResponse,
		orgUser,
		open,
		showError
	] );

	// We set the orgOwner in a separate useEffect so it has a value even if we're on an anonymous route (like the contract detail page)
	useEffect( () => {
		if ( orgUser && apiResponse.data?.getOrgUserWhere?.organization ) {
			setIsOrgOwner(
				rpcShared.organizations.isOwner(
					apiResponse.data.getOrgUserWhere.organization,
					apiResponse.data.getOrgUserWhere
				)
			);
		}
	}, [ orgUser, apiResponse.data?.getOrgUserWhere ] );

	useEffect( () => {
		if ( open && orgUser ) {
			updateSelf( {
				variables: {
					data: { seenOnboardingModalAt: new Date() },
				},
			} );
		}
	}, [
		open,
		orgUser,
		updateSelf
	] );

	const onClose = useCallback( () => setOpen( false ), [] );

	return {
		open: !!open,
		onClose,
		setOpen,
		isOrgOwner,
	};
};
